import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd';
import { Button, Form, Select } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import { HeaderWrapper } from 'app/components/pages/Edito/commons/Detail/HeaderWrapper/HeaderWrapper';
import { useAppSelector } from 'app/redux/hooks';
import { selectTagList } from 'app/redux/selectors/tags';
import type {
  IEditoEventForm,
  SearchCriteria,
  SearchCriteriaOption,
} from 'app/typings/edito';
import {
  searchCriteriaDefaultValue,
  searchCriteriaOptions,
} from 'app/utils/edito';

import { FormLink } from '../FormLink/FormLink';

import './SearchCriteriaForm.scss';
import { AccessibilityForm } from './SearchCriteriaValueForm/AccessibilityForm/AccessibilityForm';
import { BudgetForm } from './SearchCriteriaValueForm/BudgetForm/BudgetForm';
import { DatesForm } from './SearchCriteriaValueForm/DatesForm/DatesForm';
import { DistanceForm } from './SearchCriteriaValueForm/DistanceForm/DistanceForm';
import { LocationForm } from './SearchCriteriaValueForm/LocationForm/LocationForm';
import { PaxConfigurationForm } from './SearchCriteriaValueForm/PaxConfigurationForm/PaxConfigurationForm';
import { SortForm } from './SearchCriteriaValueForm/SortForm/SortForm';
import { TagForm } from './SearchCriteriaValueForm/TagForm/TagForm';

type Props = {
  fieldName: string | number;
  form: FormInstance<IEditoEventForm>;
};

type SearchCriteriaComponent = {
  [criteria in SearchCriteria]: React.ReactElement;
};

export const SearchCriteriaForm = ({ fieldName, form }: Props) => {
  const [selectedCriterias, setSelectedCriterias] = useState<
    (SearchCriteriaOption | undefined)[]
  >([undefined]);

  const isEndingBlock = fieldName === 'endingBlock';

  const basePathName = useMemo(
    () =>
      isEndingBlock
        ? [fieldName, 'searchFilters']
        : ['sections', fieldName, 'searchFilters'],
    [fieldName, isEndingBlock]
  );

  const nightCount = Form.useWatch([...basePathName, 'nightCount'], form);

  const tags = useAppSelector(selectTagList);

  useEffect(() => {
    const initialSelectedCriterias = searchCriteriaOptions.filter((option) => {
      return !!form.getFieldValue([...basePathName, option.value]);
    });

    if (initialSelectedCriterias.length) {
      setSelectedCriterias(initialSelectedCriterias);
    }
  }, [basePathName, fieldName, form]);

  const searchCriteriaComponents: SearchCriteriaComponent = {
    dates: <DatesForm fieldName={fieldName} />,
    pax: <PaxConfigurationForm fieldName={fieldName} />,
    sort: <SortForm fieldName={fieldName} />,
    accessibility: <AccessibilityForm fieldName={fieldName} />,
    prices: <BudgetForm fieldName={fieldName} />,
    distances: <DistanceForm fieldName={fieldName} />,
    experience: (
      <TagForm fieldName={fieldName} criteria="experience" tags={tags} />
    ),
    room: <TagForm fieldName={fieldName} criteria="room" tags={tags} />,
    situation: (
      <TagForm fieldName={fieldName} criteria="situation" tags={tags} />
    ),
    style: <TagForm fieldName={fieldName} criteria="style" tags={tags} />,
    coords: <LocationForm fieldName={fieldName} form={form} />,
  };

  const availableCriterias = useMemo(
    () =>
      searchCriteriaOptions.filter((criteria) => {
        if (criteria.value === 'dates') {
          return nightCount !== 2;
        }

        return !selectedCriterias
          // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
          .map((criteria) => criteria?.value)
          .includes(criteria.value);
      }),
    [nightCount, selectedCriterias]
  );

  const onAddSearchCriteria = () => {
    setSelectedCriterias([...selectedCriterias, undefined]);
  };

  const removeLocationFilter = () => {
    form.setFieldValue([...basePathName, 'placeId'], undefined);

    form.setFieldValue([...basePathName, 'location'], undefined);
  };

  const onSelectCriteria = (criteria: SearchCriteriaOption, index: number) => {
    const tmpCriterias = [...selectedCriterias];

    tmpCriterias[index] = criteria;

    const oldCriteria = selectedCriterias[index]?.value;

    if (oldCriteria) {
      form.setFieldValue([...basePathName, oldCriteria], undefined);

      if (oldCriteria === 'coords') {
        removeLocationFilter();
      }
    }

    searchCriteriaDefaultValue[criteria.value].forEach((defaultValue) => {
      form.setFieldValue(
        [...basePathName, ...defaultValue.name],
        defaultValue.value
      );
    });

    setSelectedCriterias(tmpCriterias);
  };

  const onDeleteCriteria = (criteria: SearchCriteria, index: number) => {
    const tmpCriterias = [...selectedCriterias];

    tmpCriterias.splice(index, 1);

    if (!tmpCriterias.length) {
      tmpCriterias.push(undefined);
    }

    form.setFieldValue([...basePathName, criteria], undefined);

    if (criteria === 'coords') {
      removeLocationFilter();
    }

    setSelectedCriterias(tmpCriterias);
  };

  useEffect(() => {
    if (nightCount === 2) {
      const datesIndex = selectedCriterias
        .map((criteria) => criteria?.value)
        .indexOf('dates');

      if (datesIndex !== -1) {
        const tmpCriterias = [...selectedCriterias];

        tmpCriterias.splice(datesIndex, 1);

        if (!tmpCriterias.length) {
          tmpCriterias.push(undefined);
        }

        setSelectedCriterias(tmpCriterias);
      }
    }
  }, [nightCount, selectedCriterias]);

  return (
    <HeaderWrapper gapTitleChildren={16} title="Search criteria">
      {selectedCriterias.map((criteria, index) => {
        return (
          <div
            key={`${criteria?.value}-${index}`}
            className="searchCriteriasContainer"
          >
            <div className="searchCriteriasSubContainer">
              <HeaderWrapper
                key={`${criteria}-${index}`}
                isBold={false}
                title={`Criteria #${index + 1}`}
              >
                <Select
                  labelInValue
                  onSelect={(value) => onSelectCriteria(value, index)}
                  options={availableCriterias}
                  value={criteria}
                  placeholder="Select a search criteria"
                />
              </HeaderWrapper>
            </div>
            <div className="searchCriteriasSubContainer">
              {criteria ? (
                <div className="criteriaValueContainer">
                  <FormLink />
                  {searchCriteriaComponents[criteria.value]}
                  <Button
                    className="deleteCriteriaButton"
                    onClick={() => onDeleteCriteria(criteria.value, index)}
                  >
                    <DeleteOutlined />
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        );
      })}

      {selectedCriterias.length < searchCriteriaOptions.length ? (
        <div className="addCriteriaButtonContainer">
          <Button onClick={onAddSearchCriteria} icon={<PlusOutlined />}>
            Add criteria
          </Button>
        </div>
      ) : null}
    </HeaderWrapper>
  );
};
